import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useProfileContext } from '../hooks/useProfileContext';
import { useAuth } from '../context/AuthContext';
import { useWriteGate, canWriteBand } from '../lib/writeGate';
import type { BandRole } from '../api';
import { isShowcaseMode } from '../lib/saveStatus';
import { isNameUnique } from '../utils/slug';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';

export default function BandListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profiles, createProfile, deleteProfile, updateProfile, setActiveProfile } = useProfileContext();
  const { user } = useAuth();
  // ── Write gate — the ACCOUNT ceiling, for the CREATE control ───────────────
  // `POST /api/bands` (the "+ New Band" control, incl. the empty state below) is
  // mounted AFTER the server's global read-only guard (server/index.js:572 vs
  // the guard :519) and delegable to no capability: the ONE account it refuses is the
  // one carrying the read-only ceiling (the demo is cut at `fetchJSON` and
  // explained by the global <ShowcaseBanner/>). The band-scoped `canWrite` would
  // ALSO freeze an account that simply has NO band yet — the very case this page
  // exists for (creating the first band) — so the create control's exact freeze
  // is the ACCOUNT ceiling the server applies. Over-freezing a legal action is
  // the same class of bug as letting an impossible one through.
  // The per-CARD Edit / Delete use the band-scoped predicate instead (below).
  // NB: these writes are gated at their CALLER on purpose: `useAuth()` THROWS
  // inside `useProfileContext` (`App.tsx` nests <AuthProvider> INSIDE
  // <ProfileProvider>), so the hook's create/update/deleteProfile stay ungated.
  const writeGate = useWriteGate();
  const isShowcase = !!user?.is_showcase || isShowcaseMode();
  const isSuperAdmin = user?.global_role === 'superadmin';
  const canWrite = !writeGate.readOnlyReason && !isShowcase;
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !canWrite;
  // ── Per-CARD exactness (2026-09-25) ───────────────────────────────────────
  // `canWrite` above is the ACCOUNT ceiling: it is what `POST /api/bands` (the
  // "+ New Band" control) refuses, and it MUST stay account-level — a user with
  // no band yet must still create their first one (the empty state exists for
  // exactly that). But the Edit / Delete of a CARD act on ONE band: `PUT
  // /api/bands/:id` needs editor+ on THAT band (server/routes/bands.js:114) and
  // `DELETE` needs **owner** (:143), both mounted AFTER the read-only guard
  // (server/index.js:572 vs the guard :519). Gating them on the account ceiling
  // alone OFFERED the action to a member who is only a `viewer` on the card's
  // band → the click always ended in a 403.
  // `canWriteBand` (writeGate.ts) is the exact per-band predicate: superadmin,
  // or owner/editor of the band WITHOUT the global read-only ceiling and WITHOUT
  // the showcase freeze. An owner/editor without the ceiling keeps FULL control —
  // the legitimate path is not frozen (both directions are tested).
  const canWriteCard = (p: { my_role?: string | null }): boolean =>
    canWriteBand({
      legacyRole: user?.role ?? null,
      isSuperAdmin,
      isShowcase,
      bandRole: (p.my_role ?? null) as BandRole | null,
    });
  // ── DELETE needs OWNER, not editor (2026-09-25, lot A2-bis) ───────────────
  // `router.delete('/:id', requireBandAccess('owner'))` — server/routes/bands.js:143
  // vs `PUT` at :114 (`editor`). Both used the SAME `canWriteCard` predicate, so
  // an `editor` (not owner) saw Delete ENABLED on the card and always got a 403
  // — the exact bug class this day's passes eliminated (a UI offering an action
  // the server refuses). Delete is now owner-of-that-band OR superadmin, and a
  // superadmin owns every band implicitly. The global read-only ceiling and the
  // showcase freeze still cut it (the guard runs before the router), which is why
  // this is `owner AND canWriteCard`, never a bare role test.
  const canDeleteCard = (p: { my_role?: string | null }): boolean => {
    if (isSuperAdmin) return true;
    return (p.my_role ?? null) === 'owner' && canWriteCard(p);
  };
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteName, setDeleteName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editImage, setEditImage] = useState('');
  const editingProfile = editingId ? profiles.find((p) => p.id === editingId) : undefined;
  const deleteTarget = deleteId ? profiles.find((p) => p.id === deleteId) : undefined;

  const handleCreate = () => {
    if (!canWrite) return; // read-only: never fire POST /api/bands
    const p = createProfile({ name: 'New Band' });
    // SPA navigate (NOT a full reload — reloads are fragile with the SW cache
    // and caused the band switcher to need two clicks).
    setActiveProfile(p.id).then(() => {
      navigate('/tour/shows');
    });
  };

  const handleDelete = () => {
    // Entry point 4/4 of the delete path (with the card button, the modal render
    // gate and the modal confirm button): DELETE /api/bands/:id needs OWNER on
    // THAT band (server/routes/bands.js:143) — editor is not enough.
    if (!deleteTarget || !canDeleteCard(deleteTarget)) return;
    deleteProfile(deleteTarget.id);
    setDeleteId(null);
    setDeleteName('');
  };

  const handleOpenEdit = (profile: any) => {
    setEditingId(profile.id);
    setEditName(profile.name);
    setEditImage(profile.image || '');
  };

  const handleSaveEdit = () => {
    // Per-CARD exactness: PUT /api/bands/:id needs editor+ on THAT band.
    if (!editingProfile || !canWriteCard(editingProfile)) return;
    if (editName.trim()) {
      if (!isNameUnique(editName.trim(), profiles as any, editingProfile.id)) {
        alert(t('bandList.nameExists'));
        return;
      }
      updateProfile(editingProfile.id, { name: editName.trim(), image: editImage });
      setEditingId(null);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setEditImage(event.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const sorted = [...profiles].sort(
    (a, b) => (b.lastModified ?? 0) - (a.lastModified ?? 0),
  );

  return (
    <div className="page">
      <PageHeader
        title={withPageIcon('bandList.title', t('bandList.title'))}
        actions={
          <button className="btn btn-primary" onClick={handleCreate} disabled={!canWrite}>
            + New Band
          </button>
        }
      />

      {readOnlyNote && (
        <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>
          {t('common.readOnlyAccount')}
        </p>
      )}

      {sorted.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <p style={{ color: 'var(--text-dim)', marginBottom: 16 }}>
            No band profiles yet. Create one to get started.
          </p>
          <button className="btn btn-primary" onClick={handleCreate} disabled={!canWrite}>
            + New Band
          </button>
        </div>
      )}

      <div className="profile-grid">
        {sorted.map((p) => (
          <div key={p.id} className="profile-card card">
            <div className="profile-card-image">
              {p.image ? (
                <img src={p.image} alt={p.name} />
              ) : (
                <div className="profile-card-placeholder">🎵</div>
              )}
            </div>
            <div className="profile-card-info">
              <h3>{p.name}</h3>
              <p className="profile-card-meta">
                Modified {new Date(p.lastModified ?? 0).toLocaleDateString()}
              </p>
              <div className="profile-card-actions">
                <button
                  className="btn btn-small btn-primary"
                  onClick={() => {
                    setActiveProfile(p.id).then(() => {
                      navigate('/tour/shows');
                    });
                  }}
                >
                  Open Shows
                </button>
                <button
                  className="btn btn-small btn-secondary"
                  onClick={() => handleOpenEdit(p)}
                  disabled={!canWriteCard(p)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-small btn-danger"
                  onClick={() => {
                    setDeleteId(p.id);
                    setDeleteName(p.name);
                  }}
                  disabled={!canDeleteCard(p)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delete confirmation modal — entry point 3/4: the gate is re-checked on
          RENDER, so no path (a stale `deleteId`, a future caller) can show the
          confirmation of an action the server would 403. */}
      {deleteId !== null && !!deleteTarget && canDeleteCard(deleteTarget) && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{t('bandList.deleteBand')}</span>
              <button className="modal-close" onClick={() => setDeleteId(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p>{t('bandList.confirmDelete')} <strong>{deleteName}</strong>?</p>
              <p style={{ color: 'var(--text-dim)', marginTop: 8 }}>
                This will permanently remove the band, its musicians, and all config
                data for this band.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteId(null)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={!deleteTarget || !canDeleteCard(deleteTarget)}>
                {t('bandList.deleteBand')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editingId !== null && (
        <div className="modal-overlay" onClick={() => setEditingId(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{t('bandList.editBand')}</span>
              <button className="modal-close" onClick={() => setEditingId(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Image section */}
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <div style={{ minWidth: '100px' }}>
                  {editImage ? (
                    <img
                      src={editImage}
                      alt={editName}
                      style={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '8px',
                        objectFit: 'cover',
                        border: '2px solid var(--border)',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'var(--surface)',
                        border: '2px dashed var(--border)',
                        fontSize: '40px',
                      }}
                    >
                      📸
                    </div>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    className="btn btn-small btn-primary"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Upload Image
                  </button>
                  {editImage && (
                    <button
                      className="btn btn-small btn-secondary"
                      onClick={() => setEditImage('')}
                      style={{ marginLeft: '8px' }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>

              {/* Name section */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>{t('bandList.bandName')}</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Enter band name"
                  className="form-input"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditingId(null)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveEdit}
                disabled={!editingProfile || !canWriteCard(editingProfile) || !editName.trim()}
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
